import React, { useRef, useState } from 'react';

import {
  useCreateCard, useCreateFace, useEstimateSdk, useGetLists, useMatchFaces,
} from '@vlabs/api/artemy/client';
import type {
  CardId, MatchFaceCandidateResult, SampleId, SdkDescriptor,
} from '@vlabs/api/artemy/model';
import { PlusIcon } from '@vlabs/icons';
import { Control, useModal } from '@vlabs/uikit';
import type { ButtonProps } from '@vlabs/uikit/components/controls/button';
import type { WithDataTestid } from '@vlabs/uikit/types';
import { toast } from 'react-toastify';

import { AttachFaceToCardPoint3 } from '@vlabs/monokit/features/attach-face-to-card';
import { AttachSimilarFaceToCardPoint3 } from '@vlabs/monokit/features/attach-similar-face-to-card';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import st from './AttachFaceToCardButton.module.sass';
import { useAttachFaceToCardButtonTranslation } from './i18n';
import { CreateCardFormPoint3 } from '../../entities/cards/create-card';

type State = 'attach' | 'create' | 'similar';

interface AttachFaceToCardButtonPoint3Props extends Omit<ButtonProps, 'onClick'>, WithDataTestid {
  sampleId: SampleId;
  onAttachFaceToCard?: ({ fromState, cardId }: {
    fromState: State;
    cardId: CardId;
  }) => void;
}

const displayName = 'AttachFaceToCardButtonPoint3';
const testid = buildTestid(displayName);
const LISTS_PAGE_SIZE = 1000;
const MATCHES_PER_LIST = 1;
const SIMILARITY_THRESHOLD = 0.8;

function AttachFaceToCardButtonPoint3({
  sampleId,
  onAttachFaceToCard = undefined,
  'data-testid': dataTestId = undefined,
  children,
  ...props
}: AttachFaceToCardButtonPoint3Props): React.JSX.Element {
  const { t } = useAttachFaceToCardButtonTranslation();
  const [modalContent, setModalContent] = useState<State>('attach');
  const [similarFace, setSimilarFace] = useState<MatchFaceCandidateResult>();

  const resetModalContent = () => {
    setModalContent('attach');
    setSimilarFace(undefined);
  };

  const modal = useModal({
    size: modalContent === 'create' ? 'large' : 'small',
    onRequestClose: ({ close }) => {
      resetModalContent();
      close();
    },
  });

  const closeModal = () => {
    resetModalContent();
    modal.close();
  };

  const openManualAttachModal = () => {
    resetModalContent();
    modal.open();
  };

  const showFaceCreationError = () => {
    toast.error(t('toast_error'));
  };

  const estimateSdk = useEstimateSdk();
  const createFace = useCreateFace();
  const createCard = useCreateCard();
  const getLists = useGetLists({ page_size: LISTS_PAGE_SIZE });
  const matchFaces = useMatchFaces({
    mutation: {
      onSuccess: (response) => {
        const matches = response.flatMap(({ matches: candidateMatches }) => candidateMatches.flatMap((match) => {
          if (!('result' in match)) return [];
          return match.result;
        }));
        const similar = matches
          .filter((match): match is MatchFaceCandidateResult => 'face' in match && !!match.face.card_id)
          .sort(({ similarity: left = 0 }, { similarity: right = 0 }) => right - left)[0];

        if (similar) {
          setSimilarFace(similar);
          setModalContent('similar');
        } else {
          setModalContent('attach');
        }
        modal.open();
      },
      onError: () => {
        showFaceCreationError();
        openManualAttachModal();
      },
    },
  });

  const isFacePending = estimateSdk.isPending || createFace.isPending || matchFaces.isPending;
  const isPending = createCard.isPending || isFacePending;
  const isEstimatePendingRef = useRef(false);
  const isCreateFacePendingRef = useRef(false);
  const isCreateCardPendingRef = useRef(false);

  const estimateFaceDescriptor = (
    onSuccess: (descriptor: SdkDescriptor) => void,
    onError = showFaceCreationError,
  ) => {
    if (isEstimatePendingRef.current) return;
    isEstimatePendingRef.current = true;

    estimateSdk.mutate({
      data: { samples: [sampleId] },
      params: {
        estimate_face_descriptor: 1,
        image_type: 1,
      },
    }, {
      onSuccess: (response) => {
        const imageEstimation = response.images_estimations[0];
        if (!imageEstimation || 'error' in imageEstimation) {
          onError();
          return;
        }

        const descriptor = imageEstimation.estimations[0]
          ?.face?.detection?.attributes?.descriptor?.sdk_descriptor;
        if (!descriptor) {
          onError();
          return;
        }

        onSuccess(descriptor);
      },
      onError,
      onSettled: () => {
        isEstimatePendingRef.current = false;
      },
    });
  };

  const attachSampleToCard = (cardId: CardId) => {
    if (isEstimatePendingRef.current || isCreateFacePendingRef.current) return;

    estimateFaceDescriptor((descriptor) => {
      if (isCreateFacePendingRef.current) return;
      isCreateFacePendingRef.current = true;

      createFace.mutate({
        data: {
          card_id: cardId,
          avatar: `/1/samples/faces/${sampleId}`,
          attribute: {
            face_descriptors: [descriptor],
            face_descriptor_samples: [sampleId],
          },
        },
      }, {
        onSuccess: () => {
          closeModal();
          toast.success(t('toast_success'));
          onAttachFaceToCard?.({ fromState: modalContent, cardId });
        },
        onError: showFaceCreationError,
        onSettled: () => {
          isCreateFacePendingRef.current = false;
        },
      });
    });
  };

  const openAttachModal = () => {
    const lists = getLists.data?.lists ?? [];
    if (lists.length === 0) {
      openManualAttachModal();
      return;
    }

    estimateFaceDescriptor((descriptor) => {
      matchFaces.mutate({
        data: {
          candidates: lists.map(({ list_id }) => ({
            filters: { origin: 'faces', list_id },
            limit: MATCHES_PER_LIST,
            targets: ['card_id', 'similarity'],
            threshold: SIMILARITY_THRESHOLD,
          })),
          references: [{ type: 'sdk_descriptor', id: sampleId, data: descriptor }],
        },
      });
    }, () => {
      showFaceCreationError();
      openManualAttachModal();
    });
  };

  return (
    <React.Fragment>
      {modal.wrap(
        <React.Fragment>
          {modalContent === 'attach' && (
            <React.Fragment>
              <AttachFaceToCardPoint3
                isPending={isPending}
                onSubmit={attachSampleToCard}
              />
              <div className={st.Actions}>
                <Control.Button
                  className={st.ButtonWrapper}
                  disabled={isPending}
                  onClick={() => setModalContent('create')}
                  variant="outlined"
                >
                  {t('button_create')}
                </Control.Button>

                <Control.Button
                  className={st.ButtonWrapper}
                  disabled={isPending}
                  kind="secondary"
                  onClick={closeModal}
                  variant="outlined"
                >
                  {t('button_cancel')}
                </Control.Button>
              </div>
            </React.Fragment>
          )}

          {modalContent === 'similar' && !!similarFace?.face.card_id && (
            <React.Fragment>
              <AttachSimilarFaceToCardPoint3
                cardId={similarFace.face.card_id}
                isPending={isPending}
                onSubmit={() => attachSampleToCard(similarFace.face.card_id as CardId)}
                sampleId={sampleId}
                similarity={similarFace.similarity}
              />
              <div className={st.Actions}>
                <Control.Button
                  className={st.ButtonWrapper}
                  disabled={isPending}
                  onClick={() => setModalContent('attach')}
                  variant="outlined"
                >
                  {t('button_another')}
                </Control.Button>

                <Control.Button
                  className={st.ButtonWrapper}
                  disabled={isPending}
                  kind="secondary"
                  onClick={closeModal}
                  variant="outlined"
                >
                  {t('button_cancel')}
                </Control.Button>
              </div>
            </React.Fragment>
          )}

          {modalContent === 'create' && (
            <CreateCardFormPoint3
              onCancel={() => {
                if (isPending) return;
                setModalContent('attach');
              }}
              onSubmit={({ fio, lists }) => {
                if (
                  isCreateCardPendingRef.current
                  || isEstimatePendingRef.current
                  || isCreateFacePendingRef.current
                ) return;
                isCreateCardPendingRef.current = true;

                createCard.mutate({
                  data: {
                    user_data: { fio },
                    lists: lists?.length ? lists : undefined,
                  },
                }, {
                  onSuccess: ({ card_id: cardId }) => {
                    attachSampleToCard(cardId);
                  },
                  onError: () => {
                    toast.error(t('toast_create_card_error'));
                  },
                  onSettled: () => {
                    isCreateCardPendingRef.current = false;
                  },
                });
              }}
              sampleId={sampleId}
            />
          )}
        </React.Fragment>,
      )}

      <Control.Button
        icon={<PlusIcon />}
        kind="secondary"
        onClick={openAttachModal}
        variant="filled"
        {...testid(dataTestId)}
        {...props}
        disabled={isPending || getLists.isLoading || props.disabled}
      >
        {children ?? t('button_face_to_card')}
      </Control.Button>
    </React.Fragment>
  );
}

AttachFaceToCardButtonPoint3.displayName = displayName;

export { AttachFaceToCardButtonPoint3 };
